import { FunctionComponent, useContext } from "react";
import { IScreenProps } from "../Screen";
import { Query } from "@apollo/client/react/components";
import { QueryResult } from "@apollo/client";
import { Notifier } from "../../view/Notifier";
import { ReleaseFrequency, Role, UserPreferenceTypes } from "../../graphql";
import { Breadcrumbs } from "../../view/Navigation/BreadCrumbs";
import { ApplicationContext } from "../../components/ApplicationProvider";
import { UserContext } from "../../components/User/UserProvider";
import {
    UPDATE_SOFTWARE_VERSIONS,
    VERSION_MANAGEMENT_GET_FORM_DATA,
    VersionManagementGetFormDataResult,
} from "./VersionManagementQueries";
import { UpdateVersionsData, VersionManagementForm } from "./components/VersionManagementForm";
import { InfoBox } from "../../components/Styles";
import { useUserPreference } from "../../graphql/Queries/UserPreferences";
import { useMutationThatWorksWithHMR } from "./hooks/useMutationThatWorksWithHMR";
import { mapQueryResultToVersionManagementState } from "./utils/version_management_utils";
import { OrangeInfoBox } from "./components/OrangeInfoBox";

/**
 * Version management screen
 */
export const VersionManagement: FunctionComponent<IScreenProps> = () => {
    const { t } = useContext(ApplicationContext);
    const { isCompanyAdmin, role } = useContext(UserContext);
    const isReadOnly = !isCompanyAdmin && role !== Role.GlobalAdmin;
    const breadCrumbs = <Breadcrumbs sections={[{ label: t("version_management.breadcrumb") }]} />;

    const [updateCompanySoftwareVersion, updateVersionsMutationState] = useMutationThatWorksWithHMR(
        UPDATE_SOFTWARE_VERSIONS,
        {
            onError: (error) => Notifier.error({ message: t("version_management.update_error") }),
            onCompleted: () => Notifier.success(t("version_management.update_success")),
        }
    );

    const { value: isInfoBoxCollapsedStr, loading: loadingPreferences } = useUserPreference(
        UserPreferenceTypes.VERSION_MANAGEMENT_INFO_BOX_COLLAPSED
    );

    return (
        <Query<VersionManagementGetFormDataResult, { releaseFrequency?: ReleaseFrequency }>
            query={VERSION_MANAGEMENT_GET_FORM_DATA}
        >
            {({ loading, error, data }: QueryResult<VersionManagementGetFormDataResult>) => {
                if (loading || loadingPreferences) {
                    return <div className="ui loader active" />;
                }

                if (error) {
                    Notifier.error(error);
                    return null;
                }
                if (loading || !data) {
                    return <div className="ui loader active" />;
                }

                const versionManagementState = mapQueryResultToVersionManagementState(data);

                const updateVersions = async ({ company, groups }: UpdateVersionsData) => {
                    try {
                        await updateCompanySoftwareVersion({
                            variables: {
                                company,
                                groups,
                            },
                            awaitRefetchQueries: true,
                            refetchQueries: [
                                {
                                    query: VERSION_MANAGEMENT_GET_FORM_DATA,
                                },
                            ],
                        });
                    } catch (err) {
                        Notifier.error({ message: t("version_management.update_error") });
                    }
                };

                return (
                    <div style={{ paddingBottom: 25, maxWidth: 550 }}>
                        {breadCrumbs}
                        <h2 style={{ paddingBottom: "32px" }} className="page-header">
                            {t("version_management.breadcrumb")}
                        </h2>

                        <InfoBox>{t("version_management.description")}</InfoBox>

                        <OrangeInfoBox
                            initiallyCollapsed={isInfoBoxCollapsedStr === "true"}
                            heading={t("version_management.multiple_account_warning.heading")}
                            content={
                                <ul style={{ margin: 0, paddingLeft: "16px" }}>
                                    <li>
                                        {t("version_management.multiple_account_warning.info_1")}
                                    </li>
                                    <li>
                                        {t("version_management.multiple_account_warning.info_2")}
                                    </li>
                                </ul>
                            }
                        />

                        <VersionManagementForm
                            company={versionManagementState.company}
                            groups={versionManagementState.groups}
                            softwareTags={versionManagementState.softwareTags}
                            updateVersions={updateVersions}
                            isUpdatingVersions={updateVersionsMutationState.loading}
                            isReadOnly={isReadOnly}
                            t={t}
                        />
                    </div>
                );
            }}
        </Query>
    );
};
